/**
 * Account import — pure helpers behind the "Import accounts" flow in
 * the Accounts CRM view (and the one-click ArgoIQ dogfood seed).
 *
 * Everything here is deliberately side-effect free so the view stays
 * thin: parse pasted text into clean names, filter out names that
 * already exist, and build the HONEST minimal create-payload for each.
 *
 * Honesty rule: an imported account gets its real name and nothing
 * else — no fabricated POCs, revenue, stages, or workflow status. The
 * `Account` type (src/lib/db/types.ts) only *requires* `name` on the
 * create-payload (`NewAccountInput`); workflows/audit/emails are
 * defaulted by `createAccount`. Note the Account record has no
 * `status` field — an account is implicitly active by existing — so
 * the draft carries no invented status either.
 */

import type { NewAccountInput } from '../db/accounts';

/**
 * ARGO's own real customers — names only. Used by the "Load ArgoIQ
 * customers" dogfood button so the app runs on real accounts instead
 * of fiction. No metrics, contacts, or revenue are attached.
 */
export const ARGOIQ_CUSTOMERS: readonly string[] = [
  'ArgoIQ',
  'TRIBE HQ',
  'Sams List',
  'Engaged Auto',
  'Marketing Nerdy',
  'Drift Peptides',
  'Optimal Data Consulting',
  'Karmic Buzz',
];

/**
 * Parse a pasted blob of company names into a clean list.
 *
 * - splits on newlines AND commas (people paste both shapes)
 * - trims whitespace around each entry
 * - drops blank entries
 * - dedupes case-insensitively, keeping the FIRST casing seen
 */
export function parseAccountNames(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[\n,]/)) {
    const name = raw.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

/**
 * Filter `names` down to the ones NOT already present in
 * `existingNames` (case-insensitive). Makes re-running an import
 * idempotent: names that already exist as accounts are skipped.
 */
export function filterNewNames(
  names: string[],
  existingNames: string[],
): string[] {
  const existing = new Set(existingNames.map((n) => n.trim().toLowerCase()));
  return names.filter((n) => !existing.has(n.trim().toLowerCase()));
}

/**
 * Minimal valid create-payload for an imported account: the real name
 * plus empty defaults for the required collections. Nothing invented.
 */
export function buildAccountDraft(name: string): NewAccountInput {
  return {
    name: name.trim(),
    workflows: [],
  };
}
